import { CommonModule } from '@angular/common';
import { ModuleWithProviders, NgModule } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { RouterModule } from '@angular/router';
import { I18NextModule } from 'angular-i18next';
import {
    AlertCircle,
    AlignHorizontalDistributeCenter,
    AlignVerticalSpaceBetween,
    ArrowDown,
    ArrowLeft,
    ArrowRight,
    ArrowUp,
    Bold,
    BookOpen,
    ChartColumn,
    Check,
    Circle,
    Columns,
    Columns3,
    CornerDownRight,
    Eraser,
    Highlighter,
    Image,
    Italic,
    LUCIDE_ICONS,
    Landmark,
    List,
    ListIndentDecrease,
    ListIndentIncrease,
    ListOrdered,
    LoaderCircle,
    LucideAngularModule,
    LucideIconProvider,
    Minus,
    NotebookPen,
    PanelLeftClose,
    PanelLeftOpen,
    Percent,
    Pilcrow,
    Redo,
    RemoveFormatting,
    Rows,
    Rows3,
    SeparatorHorizontal,
    Sigma,
    Strikethrough,
    Table,
    TableCellsMerge,
    TableCellsSplit,
    TableColumnsSplit,
    TableRowsSplit,
    TextAlignCenter,
    TextAlignEnd,
    TextAlignJustify,
    TextAlignStart,
    TextSelect,
    Trash2,
    Underline,
    Undo,
    Undo2,
    Users,
    // REV-21057 — icons used by the SharePoint folder-selection view.
    Cloud,
    Folder,
    Upload,
    X,
    Plus,
    // REV-21354 — the Setup-Wizard: the API-connection card and the accounting-data summary row.
    Link,
    Database,
    // REV-21367 — icons of the ABCP closing document hub. Names taken verbatim from the AWF
    // prototype's `class="lucide lucide-<name>"`, so the visual contract needs no translation.
    BotMessageSquare,
    MessageSquare,
    ChevronDown,
    ChevronsUpDown,
    Clock,
    Eye,
    EyeOff,
    FileText,
    // REV-21964 — die Upstream-Zustände (C1–C4) und der Alias (ID2) im Dokumentenhub:
    // `Unlink` = in SharePoint gelöscht oder aus dem verbundenen Ordner verschoben,
    // `History` = durch eine neuere Fassung ersetzt, `Copy` = derselbe Inhalt unter zweitem Namen.
    // ⚠️ Fehlt eines davon, wirft die Pure View beim Rendern — siehe die Warnung im zweiten Block.
    Copy,
    History,
    Unlink,
    // REV-21368 — `Ändern` auf der grünen Zuordnungs-Karte der Einzeldokument-Seite.
    Pencil,
    RefreshCw,
    Search,
    Settings,
    Sparkles,
    // REV-22096 — the three feature icons on the closing landing page (`Scale`, `ShieldCheck`; `FileText` above).
    Scale,
    ShieldCheck,
    // REV-22096 — the amber warnings of the closing setup (wizard + host) name `triangle-alert`;
    // lucide-angular exports it as `TriangleAlert`, and without it those boxes rendered no icon.
    TriangleAlert,
    // #84 — N1-Meldung (AlertCircle: der Schreibfehler), N2-Ruecknahme (Undo2) und der laufende
    // Schreibvorgang (LoaderCircle).
    // `ham-button`'s `lucideIcon`, for icons `assets/hamIcons` does not have.
    Printer,
    Send,
    // Glyphs that replaced characters and emojis in ABCP: the project file, the PDF page buttons.
    ClipboardList,
    ChevronLeft,
    ChevronRight,
    // REV-22476 — the Jahresabschluss overview: a proof that is missing.
    FileExclamationPoint,
} from 'lucide-angular';
import {
    HamAvatarComponent,
    HamBadgeToggleComponent,
    HamPageHeaderComponent,
    HamPaginationComponent,
    HamTabNavigationComponent,
    HamTableComponent,
} from '../public-api';
import { HamAccordionItemComponent } from './components/accordion-item/accordion-item.component';
import { HamAccordionComponent } from './components/accordion/accordion.component';
import { AnnouncementBannerComponent } from './components/announcement-banner/announcement-banner.component';
import { HamBadgeButtonComponent } from './components/badgeButton/badgeButton.component';
import { HamFilterPillComponent } from './components/ham-filter-pill/ham-filter-pill.component';
import { HamTagPillComponent } from './components/ham-tag-pill/ham-tag-pill.component';
import { HamSegmentedToggleComponent } from './components/ham-segmented-toggle/ham-segmented-toggle.component';
import { HamMenuButtonComponent } from './components/ham-menu-button/ham-menu-button.component';
import { BulkEditActionBarComponent } from './components/bulk-edit-action-bar/bulk-edit-action-bar.component';
import { HamButtonComponent } from './components/button/button.component';
import { HamConfirmDeclineModalComponent } from './components/confirm-decline-modal/confirm-decline-modal.component';
import { DateFilterDropDown } from './components/date-filter-drop-down/date-filter-drop-down.component';
import { DatePickerFlyoutComponent } from './components/date-picker-flyout/date-picker-flyout.component';
import { FilterComponent } from './components/filter/filter.component';
import { HamDocumentFormComponent } from './components/ham-document-form/ham-document-form.component';
import { HamGeneralConfirmDeclineModalBodyComponent } from './components/ham-general-confirm-decline-modal-body/ham-general-confirm-decline-modal-body.component';
import { HamInlineTableCellEditComponent } from './components/ham-inline-table-cell-edit/ham-inline-table-cell-edit.component';
import { HamPageTabNavigationComponent } from './components/ham-page-tab-navigation/ham-page-tab-navigation.component';
import { HamReferencingInputComponent } from './components/ham-referencing-input/ham-referencing-input.component';
import { HamSideBarComponent } from './components/ham-side-bar/ham-side-bar.component';
import { HamNavigationBarComponent } from './components/navigation-bar/navigation-bar.component';
import { HamStylingMenuComponent } from './components/ham-styling-menu/ham-styling-menu.component';
import { HamTableWithActionsComponent } from './components/ham-table-with-actions/ham-table-with-actions.component';
import { HamTiptapIconComponent } from './components/ham-tiptap-icon/ham-tiptap-icon.component';
import { HamToggle } from './components/ham-toggle/ham-toggle.component';
import { HeaderSaveActionsComponent } from './components/header-save-actions/header-save-actions.component';
import { InnerPageTabNavigationComponent } from './components/inner-page-tab-navigation/inner-page-tab-navigation.component';
import { MetaInfoComponent } from './components/meta-info/metaInfo.component';
import { HamModalComponent } from './components/modal/modal.component';
import { NotesActionsPaletteComponent } from './components/notes-actions-pallete/notes-actions-palette.component';
import { HamSubscriptionTiersComponent } from './components/tier-switch/ham-subscription-tiers.component';
import { HamTiptapToolbarComponent } from './components/tiptap-toolbar/tiptap-toolbar.component';
import { UpdateBannerComponent } from './components/update-banner/update-banner.component';
import { UploadDocumentComponent } from './components/upload-document/upload-document.component';
import { UploadComponent } from './components/upload/upload.component';
import { UserFilterDropDown } from './components/user-filter-drop-down/user-filter-drop-down.component';
import {
    HamBadgeColDirective,
    HamBadgeDirective,
    HamCellDirective,
    HamCheckboxDirective,
    HamDateColDirective,
    HamFlyoutDirective,
    HamFormatDirective,
    HamHeaderCellDirective,
    HamHighlighAltColDirective,
    HamHighlighColDirective,
    HamIconShapesDirective,
    HamMenuButtonDirective,
    HamNumberColDirective,
    HamTableDirective,
    HamTextColDirective,
} from './directives';
import { HamSimpleDropDownDirective } from './directives/forms/dropDown.directive';
import { HamInputDirective } from './directives/forms/input.directive';
import { HamRadioDirective } from './directives/forms/radio.directive';
import { HamSearchDropDownDirective } from './directives/forms/searchDropDown.directive';
import { HamDisabledTooltipDirective } from './directives/toolTip/disabled-tooltip.directive';
import { HamTooltipDirective } from './directives/toolTip/tool-tip.directive';

const ANGULAR_MATERIAL_MODULES = [MatDatepickerModule, MatCheckboxModule];

const LUCIDE_ICON_SET = {
    AlertCircle,
    AlignHorizontalDistributeCenter,
    AlignVerticalSpaceBetween,
    ArrowDown,
    ArrowLeft,
    ArrowRight,
    ArrowUp,
    Bold,
    BookOpen,
    ChartColumn,
    Check,
    Circle,
    Columns,
    Columns3,
    CornerDownRight,
    Eraser,
    Highlighter,
    Image,
    Italic,
    Landmark,
    List,
    ListIndentDecrease,
    ListIndentIncrease,
    ListOrdered,
    LoaderCircle,
    Minus,
    NotebookPen,
    PanelLeftClose,
    PanelLeftOpen,
    Percent,
    Pilcrow,
    Redo,
    RemoveFormatting,
    Rows,
    Rows3,
    SeparatorHorizontal,
    Sigma,
    Strikethrough,
    Table,
    TableCellsMerge,
    TableCellsSplit,
    TableColumnsSplit,
    TableRowsSplit,
    TextAlignCenter,
    TextAlignEnd,
    TextAlignJustify,
    TextAlignStart,
    TextSelect,
    Trash2,
    Underline,
    Undo,
    Undo2,
    Users,
    // REV-21057 — icons used by the SharePoint folder-selection view.
    Cloud,
    Folder,
    Upload,
    X,
    Plus,
    // REV-21354 — the Setup-Wizard: the API-connection card and the accounting-data summary row.
    Link,
    Database,
    // REV-21367 — ABCP closing document hub. ⚠️ A `development` merge has silently deleted
    // additions to this set before (see CLAUDE.md); without them the pure view throws on render.
    BotMessageSquare,
    MessageSquare,
    ChevronDown,
    ChevronsUpDown,
    Clock,
    Copy,
    Eye,
    EyeOff,
    FileText,
    History,
    Pencil,
    RefreshCw,
    Search,
    Settings,
    Unlink,
    Sparkles,
    // REV-22096 — the three feature icons on the closing landing page (`Scale`, `ShieldCheck`; `FileText` above).
    Scale,
    ShieldCheck,
    // REV-22096 — the amber warnings of the closing setup (wizard + host) name `triangle-alert`;
    // lucide-angular exports it as `TriangleAlert`, and without it those boxes rendered no icon.
    TriangleAlert,
    // #84 — N1 (AlertCircle, LoaderCircle) and N2 (Undo2) of the ABCP machine-assignment wiring.
    // Kept ALSO in this const, not just the import above: this is the object the provider actually
    // reads (see the REV-21367 warning above it).
    // `ham-button`'s `lucideIcon`, for icons `assets/hamIcons` does not have.
    Printer,
    Send,
    // Glyphs that replaced characters and emojis in ABCP: the project file, the PDF page buttons.
    ClipboardList,
    ChevronLeft,
    ChevronRight,
    FileExclamationPoint,
};

const EXPORTED_DECLARATIONS = [
    UpdateBannerComponent,
    HamButtonComponent,
    HamBadgeToggleComponent,
    HamModalComponent,
    HamAvatarComponent,
    HamPageHeaderComponent,
    HamPageTabNavigationComponent,
    HamInputDirective,
    HamSearchDropDownDirective,
    HamTabNavigationComponent,
    InnerPageTabNavigationComponent,
    HamTableComponent,
    HamTableWithActionsComponent,
    HamPaginationComponent,
    HamTableDirective,
    HamHeaderCellDirective,
    HamCellDirective,
    HamNumberColDirective,
    HamTextColDirective,
    HamDateColDirective,
    HamHighlighColDirective,
    HamHighlighAltColDirective,
    HamBadgeColDirective,
    HamIconShapesDirective,
    HamBadgeDirective,
    HamFlyoutDirective,
    HamCheckboxDirective,
    FilterComponent,
    HamAccordionItemComponent,
    HamAccordionComponent,
    HamConfirmDeclineModalComponent,
    HamInlineTableCellEditComponent,
    HamSimpleDropDownDirective,
    HamGeneralConfirmDeclineModalBodyComponent,
    HeaderSaveActionsComponent,
    HamSideBarComponent,
    HamNavigationBarComponent,
    NotesActionsPaletteComponent,
    HamReferencingInputComponent,
    HamTooltipDirective,
    HamMenuButtonDirective,
    MetaInfoComponent,
    UploadComponent,
    HamToggle,
    HamRadioDirective,
    HamStylingMenuComponent,
    HamFormatDirective,
    HamDisabledTooltipDirective,
    UploadDocumentComponent,
    HamTiptapIconComponent,
    HamTiptapToolbarComponent,
    HamBadgeButtonComponent,
    HamFilterPillComponent,
    HamTagPillComponent,
    HamSegmentedToggleComponent,
    HamMenuButtonComponent,
    UserFilterDropDown,
    DateFilterDropDown,
    HamDocumentFormComponent,
    BulkEditActionBarComponent,
    HamSubscriptionTiersComponent,
    DatePickerFlyoutComponent,
    AnnouncementBannerComponent,
];

@NgModule({
    declarations: EXPORTED_DECLARATIONS,
    imports: [
        CommonModule,
        FormsModule,
        RouterModule,
        FlexLayoutModule,
        ReactiveFormsModule,
        I18NextModule,
        ANGULAR_MATERIAL_MODULES,
        LucideAngularModule,
    ],
    exports: EXPORTED_DECLARATIONS,
    providers: [{ provide: LUCIDE_ICONS, multi: true, useValue: new LucideIconProvider(LUCIDE_ICON_SET) }],
})
export class AumicoComponentsModule {
    static forRoot(): ModuleWithProviders<AumicoComponentsModule> {
        return {
            ngModule: AumicoComponentsModule,
            providers: [],
        };
    }
}
